import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Badge,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tag,
  Icon,
  Tooltip,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react';
import { SearchIcon, WarningIcon, TimeIcon } from '@chakra-ui/icons';
import Pagination from 'react-js-pagination';
import { LuBell, LuInfo, LuMail, LuMessageCircle, LuMessageSquare, LuPhone } from 'react-icons/lu';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { FaMobileAlt } from 'react-icons/fa';
import { fetchWrapper } from '../../../../helpers';
import moment from 'moment';
import { getUserTimeZone } from '../../../../utils/DefaultFunction';

const MessageDetailsTable = ({ messageData, loading, error, onRetry, onPageChange }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [statusList, setStatusList] = useState([]);
  const debounceTimer = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const { isOpen: isErrorLogOpen, onOpen: onErrorLogOpen, onClose: onErrorLogClose } = useDisclosure();
  const [selectedErrorLog, setSelectedErrorLog] = useState(null);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    onPageChange(newPage, pageSize, statusFilter, channelFilter, searchQuery);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1);
    onPageChange(1, parseInt(newPageSize), statusFilter, channelFilter, searchQuery);
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
    onPageChange(1, pageSize, newStatus, channelFilter, searchQuery);
  };

  const handleChannelFilterChange = (newChannel) => {
    setChannelFilter(newChannel);
    setCurrentPage(1);
    onPageChange(1, pageSize, statusFilter, newChannel, searchQuery);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setCurrentPage(1);
      onPageChange(1, pageSize, statusFilter, channelFilter, value);
    }, 500);
  };

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    setIsOpen(true)
    // onOpen();
  };

  const getStatusList = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/status/list`;
      const res = await fetchWrapper.get(url);
      setStatusList(res?.data || res || []);
    } catch (err) {
      console.error("Failed to load status list:", err);
    }
  };

  useEffect(() => {
    getStatusList();
  }, []);


  const getStatusColor = (status) => {
    if (!status || typeof status !== "string") return "gray";
    const statusLower = status.toLowerCase();
    if (statusLower.includes("delivered")) return "green";
    if (statusLower.includes("sent")) return "blue";
    if (statusLower.includes("queued")) return "yellow";
    if (statusLower.includes("failed") || statusLower.includes("error")) return "red";
    return "gray";
  };

  const getChannelColor = (channel) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return 'green';
      case 'sms':
        return 'blue';
      case 'email':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime || dateTime === '-') return '-';
    return dateTime;
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">Loading message details...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={onRetry} colorScheme="blue">
          Retry
        </Button>
      </Box>
    );
  }

  if (!messageData) {
    return (
      <Box p={6} textAlign="center">
        <Text color="gray.500">No message data available</Text>
      </Box>
    );
  }

  const regexCheckHtml = /<[^>]*>/;


  const Channels = [
    {
      name: "Whatsapp",
      icon: LuMessageCircle,
      color: "#00a63e",
      description: "Rich messaging with media and buttons",
      id: 2,
    },
    {
      name: "SMS",
      icon: LuMessageSquare,
      color: "#155dfc",
      description: "Simple text messages",
      id: 1,
    },
    {
      name: "E-Mail",
      icon: LuMail,
      color: "#9810fa",
      description: "HTML emails with rich content",
      id: 3,
    },
    {
      name: "Push Notification",
      icon: LuBell,
      color: "#e60076",
      description: "Mobile app notifications",
      id: 6,
    },
    {
      name: "In-App Notification",
      icon: LuBell,
      color: "#4f39f6",
      description: "In-app messages and alerts",
      id: 5,
    },
    {
      name: "Voice (IVR)",
      icon: LuPhone,
      color: "#f54a00",
      description: "Automated voice calls",
      id: 4,
    },
  ];





  const getChannelInfo = (channelIdOrName, statusId) => {

    return (
      statusId !== 1 ? Channels.find(
        (c) => c.id === channelIdOrName || c.name === channelIdOrName
      ) : { name: "System", icon: FaMobileAlt, color: "gray" }

    );
  };
  const getChannelName = (statusId) => {

    let status = statusList.find(
      (c) => c.id === statusId
    )

    return status?.name
  };


  const closeModal = () => {
    setIsOpen(false);
    setSelectedMessage(null);
  };


  const getStatusDetails = (statusId) => {
    return statusList.find((s) => s.status_id === statusId || s.id === statusId);
  };

  // status_id 110 isn't in the /status/list response, so getStatusDetails finds
  // nothing for it — the journey falls back to this instead of a bare "Status 110"
  // with a failure (✗) icon, since a missing msg_status reads as not-success.
  const KNOWN_STATUS_FALLBACKS = { 110: { name: 'Sent to device', msg_status: '1' } };

  const { individual_messages, pagination, filters } = messageData;
  let avialble_filter = [{ name: "queued", id: 1, displayName: "Queued" }, { name: "clicked", id: 1, displayName: "Clicked" }, { name: "sent", id: 2, displayName: "Message Sent" }, { name: "delivered", id: 3, displayName: "Message Delivered" }, { name: "read", id: 4, displayName: "Message Read" }, { name: "failed", id: 5, displayName: "Message Failed" }, { name: "undelivered", id: 6, displayName: "Message Undelivered" }]
  const getTypeId = (typeId) => {
    switch (typeId) {
      case 1:
        return "Authentication";
      case 2:
        return "Transactional";
      case 3:
        return "Marketing";
      case 4:
        return "Informational";
      default:
        return "Unknown";
    }
  }
  return (
    <Box>
      {/* Filters and Controls */}
      <Card p={4} mb={6} borderRadius="lg" shadow="sm">
        <VStack spacing={4} align="stretch">
          <Text fontWeight="semibold" fontSize="lg" color="gray.800">
            Message Filters
          </Text>

          <Flex direction={{ base: 'column', md: 'row' }} gap={4} align="end">
            <Box flex="2">
              <Text fontSize="sm" color="gray.600" mb={2}>Search</Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search by recipient, name, message ID..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </InputGroup>
            </Box>

            <Box flex="1">
              <Text fontSize="sm" color="gray.600" mb={2}>Status</Text>
              <Select
                placeholder="All Statuses"
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
              >
                {avialble_filter?.map((status) => (
                  <option key={status.id} value={status.name}>
                    {status?.displayName}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex="1">
              <Text fontSize="sm" color="gray.600" mb={2}>Channel</Text>
              <Select
                placeholder="All Channels"
                value={channelFilter}
                onChange={(e) => handleChannelFilterChange(e.target.value)}
              >
                {filters?.available_channels?.map((channel) => (
                  <option key={channel} value={channel}>
                    {channel}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>Per Page</Text>
              <Select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                width="100px"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Select>
            </Box>

            <Button
              onClick={() => {
                setStatusFilter('');
                setChannelFilter('');
                setSearchQuery('');
                setCurrentPage(1);
                onPageChange(1, pageSize, '', '', '');
              }}
              variant="outline"
            >
              Clear Filters
            </Button>
          </Flex>
        </VStack>
      </Card>

      {/* Messages Table */}
      <Card p={0} borderRadius="lg" shadow="sm" overflow="hidden">
        <Box p={4} borderBottom="1px solid #e2e8f0" bg="gray.50">
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                Message Details
              </Text>
              <Text fontSize="sm" color="gray.600">
                Showing {pagination?.showing || 0} of {pagination?.total?.toLocaleString() || 0} messages
              </Text>
            </Box>
          </Flex>
        </Box>

        <Box overflowX="auto">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f7fafc' }}>
              <tr>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  Message ID
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  Recipient
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  Channel
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  Status
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  Sent Date
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  Delivered At
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  Read
                </th>
              </tr>
            </thead>
            <tbody>

              {individual_messages?.length > 0 ? individual_messages?.map((message, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f7fafc' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      color="blue.600"
                      cursor="pointer"
                      textDecoration="underline"
                      _hover={{ color: 'blue.800' }}
                      onClick={() => handleMessageClick(message)}
                    >
                      {/* {message.message_id} */}
                      {message.message_id.slice(0, 16)}...
                    </Text>

                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Box>
                      <Text fontSize="14px" fontWeight="500" color="gray.800">
                        {message.recipient_name || 'Unknown'}
                      </Text>
                      <Text fontSize="12px" color="gray.600">
                        {message.recipient}
                      </Text>
                    </Box>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <Text color={"black"} >
                      {message.channel}
                    </Text>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <HStack spacing={2} justify="center">
                      <Text fontSize="sm">{getChannelName(message.status_id)}</Text>
                      {message.error_log && (
                        <Tooltip label="View error details" hasArrow>
                          {/* Plain info glyph — the status text beside it already says the
                              message failed. */}
                          <Box
                            as="span"
                            display="inline-flex" alignItems="center"
                            cursor="pointer" color="red.500"
                            _hover={{ color: 'red.600' }}
                            onClick={() => { setSelectedErrorLog(message.error_log); onErrorLogOpen(); }}
                          >
                            <Icon as={LuInfo} boxSize={4} />
                          </Box>
                        </Tooltip>
                      )}
                    </HStack>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Text fontSize="12px" color="gray.600">

                      {moment
                        .utc(message.sent_date)
                        .tz(getUserTimeZone())
                        .format("DD MMM YY hh:mm:ss A")}
                    </Text>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Text fontSize="12px" color="gray.600">

                      {message.delivered_at ? moment
                        .utc(message.delivered_at)
                        .tz(getUserTimeZone())
                        .format("DD MMM YY hh:mm:ss A") : "-"}

                    </Text>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Text fontSize="12px" color="gray.600">
                      {message.opened_at !== "-" ? moment
                        .utc(message.opened_at)
                        .tz(getUserTimeZone())
                        .format("DD MMM YY hh:mm:ss A") : "-"}
                      {/* {formatDateTime(message.opened_at)} */}
                    </Text>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" style={{ padding: '32px', textAlign: 'center', color: '#a0aec0' }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Card>

      {/* Pagination using react-js-pagination */}
      {pagination && (
        <Card p={4} mt={4} borderRadius="lg" shadow="sm">
          <Flex justify="center" align="center" flexWrap="wrap" gap={4}>


            <Box>
              <Pagination
                activePage={pagination.page}
                itemsCountPerPage={pageSize}
                totalItemsCount={pagination.total}
                pageRangeDisplayed={5}
                onChange={handlePageChange}
                itemClass="page-item"
                linkClass="page-link"
                activeClass="active"
                disabledClass="disabled"
                hideNavigation={false}


              />
            </Box>
          </Flex>
        </Card>
      )}

      {/* Message Details Modal */}
      <Modal isOpen={isOpen} onClose={closeModal} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="600px">
          <ModalHeader
            borderBottom="1px"
            borderColor="gray.200"
            pb={3}
            fontSize="lg"
            fontWeight="semibold"
          >
            <HStack spacing={2}>
              <Icon as={TimeIcon} color="blue.500" />
              <Text>Message Delivery Journey</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            {selectedMessage ? (
              <VStack align="stretch" spacing={4}>
                {/* Message ID */}
                <Box bg="gray.50" p={3} borderRadius="md">
                  <Text fontSize="xs" color="black" mb={1}>
                    <strong>{selectedMessage.message_id || selectedMessage._id}</strong>
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Complete delivery timeline showing channel-specific status
                    progression and auto-failover attempts.
                  </Text>
                </Box>

                {/* Info Grid */}
                <Box
                  bg="blue.50"
                  border="1px"
                  borderColor="blue.200"
                  borderRadius="md"
                  p={3}
                >
                  <Flex justify="space-between" mb={2}>
                    <Box flex="1">
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Recipient
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {selectedMessage.recipient}
                      </Text>
                    </Box>

                    <Box flex="1">
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Message Type
                      </Text>
                      <Tag fontSize="xs" fontWeight="normal" border={"1px"} borderColor="gray.300"
                        color={"black"}
                        bg="white">
                        {getTypeId(selectedMessage.message_type_id)}

                      </Tag>
                    </Box>
                    <Box flex="1">
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Final Status
                      </Text>
                      <Badge
                        colorScheme={getStatusColor(selectedMessage?.status)}
                        px={2}
                        py={1}
                        borderRadius="md"
                      >
                        {selectedMessage?.status || "Unknown"}
                      </Badge>
                    </Box>


                  </Flex>
                  {selectedMessage?.email && (
                    <Box flex="1">
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Email
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {selectedMessage.email || '-'}
                      </Text>
                    </Box>
                  )}

                  <Box flex="1">
                    <Text fontSize="xs" color="gray.600" mb={1}>
                      Subscriber ID
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold">
                      {selectedMessage?.subscriber_id || '-'}
                    </Text>
                  </Box>
                  {/* <Box>
                    <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">
                      Message Content
                    </Text>
                    <Box bg="white" border={1} borderColor={"gray.100"} p={3} borderRadius="md">
                      {regexCheckHtml.test(selectedMessage.message_content) ? <Box
                        dangerouslySetInnerHTML={{ __html: selectedMessage.message_content }}
                        fontSize="sm"
                        color="gray.800"
                      /> : <Text fontSize="sm">{selectedMessage.message_content}</Text>}

                    </Box>
                  </Box> */}

                </Box>


                {/* Delivery Timeline */}
                <Box>
                  <HStack spacing={2} mb={3}>
                    <Icon as={TimeIcon} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" fontWeight="semibold">
                      Delivery Timeline & Status Progression
                    </Text>
                  </HStack>

                  <VStack align="stretch" spacing={0} pl={2}>
                    {selectedMessage.history?.map((h, i) => {
                      const status = getStatusDetails(h.status_id);
                      const statusFallback = KNOWN_STATUS_FALLBACKS[h.status_id];
                      const displayStatusName =
                        status?.name || statusFallback?.name || `Status ${h.status_id}`;
                      const isSuccessStatus =
                        (status?.msg_status ?? statusFallback?.msg_status) === "1";


                      const { name: channelName, icon: ChannelIcon, color: channelColor } = getChannelInfo(h.channel_id, h.status_id);



                      const isFailed = status?.name?.toLowerCase().includes("fail") ||
                        status?.name?.toLowerCase().includes("undeliverable");

                      // Find the current and next items in the history array
                      const currentIndex = selectedMessage.history.findIndex((item) => item === h);
                      const nextStatus = selectedMessage.history[currentIndex + 1];
                      const nextStatusDetails = nextStatus ? getStatusDetails(nextStatus.status_id) : null;

                      const isNextNotFailed =
                        nextStatusDetails &&
                        !nextStatusDetails?.name?.toLowerCase().includes("fail") &&
                        !nextStatusDetails?.name?.toLowerCase().includes("undeliverable");

                      // ✅ Trigger only when failed + next one is NOT failed
                      const shouldTriggerFailover = isFailed && isNextNotFailed;

                      const getCooloredStatus = (status) => {
                        if (!status) return "white";
                        const name = status.name?.toLowerCase() || "";

                        if (name.includes("read") || name.includes("delivered")) return "#f0fdf4";
                        if (name.includes("sent")) return "var(--brand-tint)";
                        if (name.includes("clicked")) return "#f0fdf4";
                        if (name.includes("fail") || name.includes("undeliverable")) return "#fef2f2";
                        if (name.includes("invalid") || name.includes("undeliverable")) return "#fef2f2";
                        if (name.includes("accept")) return "white";
                        return "white";
                      }
                      const getCooloredBorderedStatus = (status) => {
                        if (!status) return "gray";
                        const name = status.name?.toLowerCase() || "";

                        if (name.includes("read") || name.includes("delivered")) return "#00c951";
                        if (name.includes("clicked") || name.includes("delivered")) return "#00c951";
                        if (name.includes("sent")) return "#2b7fff";
                        if (name.includes("fail") || name.includes("undeliverable")) return "#fb2c36";
                        if (name.includes("invalid") || name.includes("undeliverable")) return "#fb2c36";
                        if (name.includes("accept")) return "#6a7282";
                        return "gray";
                      }

                      return (
                        <Flex key={i}>
                          <Box
                            flex="1"
                            bg={getCooloredStatus(status)}
                            border="1px"
                            borderColor={getCooloredStatus(status)}
                            borderLeftWidth="4px"
                            borderLeftColor={getCooloredBorderedStatus(status)}
                            borderRadius="md"
                            my={3}
                            px={2}
                            py={5}
                          >
                            {/* ✅ Left icon + Right content */}
                            <Flex align="start" gap={3}>
                              {/* Left icon */}
                              {isSuccessStatus ? (
                                <FiCheckCircle color="#00a63e" boxSize={4} />
                              ) : (
                                <FiXCircle color="#e7000b" boxSize={4} />
                              )}

                              {/* Right content */}
                              <Box flex="1">
                                <Flex justify="space-between" align="start" mb={1}>
                                  <HStack spacing={2}>
                                    {h.channel_id ? (
                                      <HStack spacing={1}>
                                        <Icon as={ChannelIcon} color={`${channelColor}`} boxSize={3.5} />
                                        <Text fontSize="xs" fontWeight="semibold">
                                          {channelName}
                                        </Text>
                                      </HStack>
                                    ) : (
                                      <Text fontSize="xs" fontWeight="semibold">
                                        {channelName}
                                      </Text>
                                    )}
                                    {status?.msg_status === "0" && <Tag bg={"red"} size={"sm"} color={"white"}>Failed</Tag>}
                                    <Tag
                                      size="sm"
                                      border="1px solid"
                                      borderColor="gray.300"
                                      color={status?.name?.toLowerCase().includes("accept") ? "black" : "#64748b"}
                                      bg="transparent"
                                    >
                                      {displayStatusName}
                                    </Tag>
                                  </HStack>

                                  <Text fontSize="xs" color="gray.500">
                                    {/* {new Date(h.time).toLocaleString("en-PK", {
                                      dateStyle: "short",
                                      timeStyle: "medium",
                                    })} */}

                                    {moment
                                      .utc(h.time)
                                      .tz(getUserTimeZone())
                                      .format("DD MMM YY hh:mm:ss A")}
                                  </Text>
                                </Flex>
                                {(h.channel_id == 5 || h.channel_id == 6) && (
                                  <Text fontSize="xs" color="gray.600">Subscriber Id : {h.subscriber_id || '-'}</Text>
                                )}
                                {h.status_id === 110 && (
                                  <Text fontSize="xs" color="gray.600">Message Sent to device</Text>
                                )}

                                {isFailed ? (
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    colorScheme="red"
                                    leftIcon={<WarningIcon />}
                                    mt={1}
                                    onClick={() => {
                                      // History entries rarely carry their own error_log — the
                                      // real payload almost always lives on the top-level message.
                                      setSelectedErrorLog(h.error_log || selectedMessage?.error_log);
                                      onErrorLogOpen();
                                    }}
                                  >
                                    Message Undeliverable — View Error Details
                                  </Button>
                                ) : (
                                  <Text fontSize="xs" color="gray.600">
                                    {status?.description ||
                                      (status?.name === "Message Accepted"
                                        ? "Message Accepted - Request received and validated"
                                        : status?.name === "Message Sent"
                                          ? `Message Sent to ${channelName}`
                                          : status?.name === "Message Delivered"
                                            ? "Message Delivered"
                                            : status?.name === "Message Read"
                                              ? "Message Read"
                                              : "Status updated")}
                                  </Text>
                                )}

                                {shouldTriggerFailover && (
                                  <Text
                                    fontSize="xs"
                                    color="#ca3500"
                                    bg="#ffedd4"
                                    px={2}
                                    py={1}
                                    mt={2}
                                    borderRadius="md"
                                    fontWeight="medium"
                                  >
                                    → Triggering auto-failover to next channel
                                  </Text>
                                )}
                              </Box>
                            </Flex>
                          </Box>
                        </Flex>
                      );
                    })}
                  </VStack>
                </Box>
              </VStack>
            ) : (
              <Text>No details available.</Text>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor="gray.200">
            <Button onClick={closeModal} colorScheme="blue" size={"sm"}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Error Log Modal */}
      <Modal isOpen={isErrorLogOpen} onClose={onErrorLogClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader borderBottom="1px" borderColor="gray.100" pb={3}>
            <HStack spacing={2}>
              <Box display="inline-flex" alignItems="center" justifyContent="center"
                w="28px" h="28px" borderRadius="full" bg="blue.50" border="1px solid" borderColor="blue.200">
                <WarningIcon color="blue.500" boxSize={3} />
              </Box>
              <Text fontSize="md" fontWeight="600" color="gray.800">Error Details</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={3} />
          <ModalBody py={4}>
            {(() => {
              const raw = selectedErrorLog;
              if (!raw) return <Text color="gray.400" fontSize="sm">No error details available.</Text>;

              let parsed = raw;
              if (typeof raw === 'string') {
                try { parsed = JSON.parse(raw); } catch { parsed = raw; }
              }

              const flattenObject = (obj, prefix = '') => {
                if (typeof obj !== 'object' || obj === null) return { [prefix]: obj };
                return Object.entries(obj).reduce((acc, [key, val]) => {
                  const newKey = prefix ? `${prefix}.${key}` : key;
                  if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
                    Object.assign(acc, flattenObject(val, newKey));
                  } else { acc[newKey] = val; }
                  return acc;
                }, {});
              };

              const renderValue = (val) => {
                if (typeof val === 'boolean') return (
                  <Tag size="sm" borderRadius="full"
                    bg={val ? '#dcfce7' : '#fee2e2'} color={val ? '#16a34a' : '#dc2626'}>
                    {String(val)}
                  </Tag>
                );
                if (val === null || val === undefined) return <Text fontSize="sm" color="gray.400">—</Text>;
                if (Array.isArray(val)) return <Text fontSize="sm" color="gray.700">{JSON.stringify(val)}</Text>;
                return <Text fontSize="sm" color="gray.700">{String(val)}</Text>;
              };

              const flatData = Array.isArray(parsed)
                // A single-item array (the overwhelmingly common case) doesn't need an
                // "[0]." prefix on every key — it only exists to disambiguate multiple
                // items, and otherwise just makes keys longer and harder to read.
                ? (parsed.length === 1
                    ? flattenObject(parsed[0])
                    : parsed.reduce((acc, item, i) => Object.assign(acc, flattenObject(item, `[${i}]`)), {}))
                : typeof parsed === 'object' ? flattenObject(parsed) : { value: parsed };

              // "value" as a raw key (e.g. a bounce webhook's `{ value: "Bounce" }`) reads
              // better to a customer as "Reason" — display-only relabel, key untouched.
              const displayKey = (key) => (key.toLowerCase() === 'value' ? 'Reason' : key);

              return (
                <TableContainer border="1px" borderColor="gray.200" borderRadius="md">
                  <Table variant="simple" size="sm" tableLayout="fixed">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th py={3} fontSize="xs" color="gray.700" fontWeight="700" w="180px">Key</Th>
                        <Th py={3} fontSize="xs" color="gray.700" fontWeight="700">Value</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(flatData).map(([key, val]) => (
                        <Tr key={key} _hover={{ bg: 'gray.50' }}>
                          <Td py={3} fontSize="sm" color="gray.600" borderColor="gray.100"
                            whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={key}>{displayKey(key)}</Td>
                          <Td py={3} borderColor="gray.100" wordBreak="break-word" whiteSpace="normal">{renderValue(val)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              );
            })()}
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor="gray.100">
            <Button onClick={onErrorLogClose} size="sm" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default MessageDetailsTable;